import type {
  PartnerLaneCreate,
  PartnerLaneOrchestration,
  PartnerLaneSortConfigurationCreate,
  PartnerLaneSortConfigurationUpdate,
  PartnerLaneUpdate,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import {
  DOCK_LANES_QUERY_KEY,
  DOCK_LANE_QUERY_KEY,
  DOCK_LANE_SCHEDULE_QUERY_KEY,
  DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY,
  DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY,
} from '~/hooks/queries/dockLaneQueryKeys';
import type { DockLaneScheduleCreateParams, DockLanePostalCodeMappingParams } from '~/store/fulfillmentContext/types';

type DockLaneUpdatePayload = PartnerLaneUpdate & { identifier: string };

type DockLaneSortConfigurationUpdatePayload = {
  laneSortConfigurationId: string;
} & PartnerLaneSortConfigurationUpdate;

const LANES_BASE = '/partner-management-proxy/api/v1/partnerConfiguration/lanes';
const SORT_CONFIGS_BASE = '/partner-management-proxy/api/v1/partnerConfiguration/laneSortConfigurations';

export const dockLaneMutations = {
  // Lane CRUD
  create: async (payload: PartnerLaneCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerLaneOrchestration>(LANES_BASE, payload);
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANES_QUERY_KEY]],
    };
  },

  update: async ({ identifier, ...body }: DockLaneUpdatePayload) => {
    const { data } = await GatewayAxiosClient.patch<PartnerLaneOrchestration>(`${LANES_BASE}/${identifier}`, body);
    return {
      data,
      // Sort configs embed lane details in their `.lanes` array (rendered by DockLaneGroupedTable),
      // so renaming a lane would leave the sort config view stale until another mutation fires.
      invalidateQueryKeys: [
        [DOCK_LANES_QUERY_KEY],
        [DOCK_LANE_QUERY_KEY, identifier],
        [DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY],
      ],
    };
  },

  delete: async (identifier: string) => {
    const { data } = await GatewayAxiosClient.delete(`${LANES_BASE}/${identifier}`);
    return {
      data,
      invalidateQueryKeys: [
        [DOCK_LANES_QUERY_KEY],
        [DOCK_LANE_QUERY_KEY, identifier],
        [DOCK_LANE_SCHEDULE_QUERY_KEY, identifier],
        [DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY],
        [DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY],
      ],
    };
  },

  // Schedule operations
  createSchedule: async (params: DockLaneScheduleCreateParams) => {
    const { laneId, ...body } = params;
    const { data } = await GatewayAxiosClient.post(`${LANES_BASE}/${laneId}/schedule/entry`, body);
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_SCHEDULE_QUERY_KEY, laneId]],
    };
  },

  deleteSchedule: async ({ laneId, laneScheduleEntryId }: { laneId: string; laneScheduleEntryId: string }) => {
    const { data } = await GatewayAxiosClient.delete(`${LANES_BASE}/${laneId}/schedule/entry/${laneScheduleEntryId}`);
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_SCHEDULE_QUERY_KEY, laneId]],
    };
  },

  // Postal code operations
  // Creating or updating mappings assigns new mappingIds that live on the sort configuration's
  // postalCodeMappingIds record. The sort config must be invalidated so downstream queries
  // refetch the updated ID list — otherwise new mappings won't appear until a hard refresh.
  putPostalCodeMappings: async (params: DockLanePostalCodeMappingParams) => {
    const { data } = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partnerConfiguration/origin/${params.originId}/laneSortConfiguration/${params.laneSortConfigurationId}/postalCodeMappings`,
      params.postalCodes,
    );
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY], [DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY]],
    };
  },

  patchPostalCodeMappings: async (params: DockLanePostalCodeMappingParams) => {
    const { data } = await GatewayAxiosClient.patch(
      `/partner-management-proxy/api/v1/partnerConfiguration/origin/${params.originId}/laneSortConfiguration/${params.laneSortConfigurationId}/postalCodeMappings`,
      params.postalCodes,
    );
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY], [DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY]],
    };
  },

  // Sort configuration operations
  updateSortConfiguration: async ({ laneSortConfigurationId, ...body }: DockLaneSortConfigurationUpdatePayload) => {
    const { data } = await GatewayAxiosClient.patch(`${SORT_CONFIGS_BASE}/${laneSortConfigurationId}`, body);
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY]],
    };
  },

  deleteSortConfiguration: async (laneSortConfigurationId: string) => {
    const { data } = await GatewayAxiosClient.delete(`${SORT_CONFIGS_BASE}/${laneSortConfigurationId}`);
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY]],
    };
  },

  createSortConfiguration: async (payload: PartnerLaneSortConfigurationCreate) => {
    const { data } = await GatewayAxiosClient.post(SORT_CONFIGS_BASE, payload);
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY]],
    };
  },

  linkSortConfigurationToOrigin: async ({
    originId,
    laneSortConfigurationId,
  }: {
    originId: string;
    laneSortConfigurationId: string;
  }) => {
    const { data } = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/origins/${originId}/laneSortConfiguration/${laneSortConfigurationId}`,
    );
    return {
      data,
      invalidateQueryKeys: [[DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY]],
    };
  },
};
